import { Controller } from "@hotwired/stimulus"

// Full-bleed monochrome Mapbox background showing architect-firm projects.
// Mapbox GL is loaded via CDN on the landing layout (window.mapboxgl); this
// controller no-ops gracefully if the library or a public token is missing, so
// the section just keeps its dark background.
//
// The map's entire look is defined in code below — not in a hosted Mapbox Studio
// style. We load Mapbox's public Standard basemap and restyle it with
// BASEMAP_CONFIG, then draw the project points with PROJECT_DOT_PAINT, whose
// opacity is animated per-dot via feature-state. Those constants are the source
// of truth for the styling; there is no Studio style to maintain.

// Monochrome night basemap (labels / POI / transit / borders / 3D all off).
const BASEMAP_CONFIG = {
  theme: "monochrome",
  lightPreset: "night",
  showPlaceLabels: false,
  showRoadLabels: false,
  showPointOfInterestLabels: false,
  showTransitLabels: false,
  showAdminBoundaries: false,
  show3dObjects: false
}

// Per-dot animation driver. A handful of random dots get their "pulse"
// feature-state ramped 0→1 at a time; the dot opacity keys off this so random
// dots gently brighten and fade — no glow, just an opacity pulse.
const pulse = ["coalesce", ["feature-state", "pulse"], 0]

// Core dot (green-600) with a black border so clustered dots stay distinct
// instead of merging into a blob. Dots rest dim green; the rolling feature-state
// pulse flashes random dots up to bright yellow at full opacity and back, so
// they fade and shift color together for an obvious twinkle.
const PROJECT_DOT_PAINT = {
  "circle-radius": 5,
  "circle-color": ["interpolate", ["linear"], pulse, 0, "#16a34a", 1, "#facc15"],
  "circle-color-use-theme": "none",
  "circle-opacity": ["interpolate", ["linear"], pulse, 0, 0.3, 1, 1],
  "circle-stroke-color": "#000000",
  "circle-stroke-width": 1.5,
  "circle-stroke-opacity": ["interpolate", ["linear"], pulse, 0, 0.4, 1, 1],
  "circle-emissive-strength": 1.5
}

const PULSE_DURATION = 900 // ms for one dot to fade out and back
const MAX_PULSES = 150 // hard cap on concurrent twinkles, independent of dot count

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    style: { type: String, default: "mapbox://styles/mapbox/standard" },
    src: String, // GeoJSON URL of project points (static export)
    center: { type: Array, default: [-74.7, 42.7] }, // NY / Northeast
    zoom: { type: Number, default: 5.6 }
  }

  connect() {
    const token = document.querySelector('meta[name="mapbox-token"]')?.content
    if (!window.mapboxgl || !token || !this.hasCanvasTarget) return

    window.mapboxgl.accessToken = token
    this.map = new window.mapboxgl.Map({
      container: this.canvasTarget,
      style: this.styleValue,
      center: this.centerValue,
      zoom: this.zoomValue,
      minZoom: 4,
      attributionControl: false,
      config: {
        // "basemap" is the Standard style's import id.
        basemap: BASEMAP_CONFIG
      }
    })

    // Background map: disable pan / zoom / scroll so the page scrolls normally
    // and the map can't be dragged. It's a non-interactive decorative field.
    ;[
      this.map.scrollZoom,
      this.map.boxZoom,
      this.map.dragRotate,
      this.map.dragPan,
      this.map.keyboard,
      this.map.doubleClickZoom,
      this.map.touchZoomRotate
    ].forEach((handler) => handler.disable())

    this.map.on("style.load", () => {
      this.map.resize()
      this.addProjects()
    })
  }

  async addProjects() {
    if (!this.hasSrcValue) return

    let data
    try {
      data = await (await fetch(this.srcValue)).json()
    } catch (error) {
      return
    }
    if (!this.map) return

    this.featureCount = data.features.length
    this.map.addSource("projects", { type: "geojson", data, generateId: true })

    // Frame the Northeast footprint of the firms (W. PA up through NY / New
    // England); the cluster stays NYC-heavy. Extra top padding drops it below
    // the headline scrim.
    this.map.fitBounds(
      [[-75.6, 40.4], [-72.2, 42.3]],
      { padding: { top: 160, bottom: 40, left: 40, right: 40 }, duration: 0 }
    )

    this.map.addLayer({
      id: "projects-dot",
      type: "circle",
      source: "projects",
      paint: PROJECT_DOT_PAINT
    })

    this.startPulsing()
  }

  // Animate a rolling set of random dots: each gets a pulse-state ramp from 0→1
  // (sine eased) over PULSE_DURATION, then a fresh random dot takes its place.
  // Paused off-screen / on hidden tabs to keep it cheap.
  startPulsing() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    if (!this.featureCount) return

    this.maxPulses = Math.min(MAX_PULSES, this.featureCount)
    this.pulses = []
    this.pulsing = false

    this.onPulseVisibility = () => this.updatePulseState()
    document.addEventListener("visibilitychange", this.onPulseVisibility)

    this.pulseObserver = new IntersectionObserver(([entry]) => {
      this.pulseInView = entry.isIntersecting
      this.updatePulseState()
    })
    this.pulseObserver.observe(this.element)
  }

  updatePulseState() {
    const shouldRun = this.pulseInView && !document.hidden
    if (shouldRun && !this.pulsing) {
      this.pulsing = true
      this.lastPulseFrame = null
      this.pulseFrame = requestAnimationFrame((t) => this.stepPulses(t))
    } else if (!shouldRun && this.pulsing) {
      this.pulsing = false
      cancelAnimationFrame(this.pulseFrame)
    }
  }

  spawnPulse() {
    return { id: Math.floor(Math.random() * this.featureCount), elapsed: 0 }
  }

  stepPulses(timestamp) {
    if (!this.map) return
    const delta = this.lastPulseFrame ? timestamp - this.lastPulseFrame : 16
    this.lastPulseFrame = timestamp

    while (this.pulses.length < this.maxPulses) {
      this.pulses.push({ ...this.spawnPulse(), elapsed: -Math.random() * PULSE_DURATION })
    }

    for (const flare of this.pulses) {
      flare.elapsed += delta
      const progress = flare.elapsed / PULSE_DURATION
      if (progress >= 1) {
        this.map.removeFeatureState({ source: "projects", id: flare.id }, "pulse")
        Object.assign(flare, this.spawnPulse())
        continue
      }
      // Stagger: negative elapsed delays the start without flashing the dot.
      const value = progress < 0 ? 0 : Math.sin(progress * Math.PI)
      this.map.setFeatureState({ source: "projects", id: flare.id }, { pulse: value })
    }

    this.pulseFrame = requestAnimationFrame((t) => this.stepPulses(t))
  }

  disconnect() {
    if (this.pulseFrame) cancelAnimationFrame(this.pulseFrame)
    if (this.onPulseVisibility) document.removeEventListener("visibilitychange", this.onPulseVisibility)
    if (this.pulseObserver) this.pulseObserver.disconnect()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
